import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import Chart from 'chart.js/auto';
import { Card, CardBody, CardHeader, CardExpandToggler } from '../other/card/card';

const PerformanceChart = forwardRef(({ platformData, selectedPlatform, selectedTeamMember }, ref) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useImperativeHandle(ref, () => ({
        getCanvas: () => chartRef.current
    }));

    useEffect(() => {
        if (!chartRef.current || !platformData.length) return;

        // Destroy existing chart
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        const themeColor = getComputedStyle(document.body).getPropertyValue('--bs-theme').trim() || '#6f42c1';
        const themeColorRgb = getComputedStyle(document.body).getPropertyValue('--bs-theme-rgb').trim() || '111, 66, 193';

        // No need to filter here - the API already filters by service_id
        const filteredData = platformData;

        const ctx = chartRef.current.getContext('2d');
        chartInstance.current = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: filteredData.map(p => p.platform),
                datasets: [{
                    label: 'Total Links',
                    data: filteredData.map(p => p.total),
                    backgroundColor: `rgba(${themeColorRgb}, 0.7)`,
                    borderColor: themeColor,
                    borderWidth: 1.5
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        backgroundColor: '#fff',
                        titleColor: '#000',
                        bodyColor: '#000',
                        borderColor: '#ddd',
                        borderWidth: 1,
                        padding: 12,
                        displayColors: false,
                        callbacks: {
                            title: (context) => {
                                const platformIndex = context[0].dataIndex;
                                const platform = filteredData[platformIndex];
                                return platform.platform;
                            },
                            label: (context) => {
                                const platformIndex = context.dataIndex;
                                const platform = filteredData[platformIndex];
                                const teamMemberData = platform.teamMembers;

                                const lines = Object.entries(teamMemberData)
                                    .filter(([, data]) => data.count > 0)
                                    .sort(([, a], [, b]) => b.count - a.count)
                                    .map(([member, data]) => `${member}: ${data.count.toLocaleString()} (${data.pct.toFixed(2)}%)`);
                                return lines.length ? lines : [`Total: ${platform.total.toLocaleString()}`];
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            callback: function(value) {
                                return value >= 1000000 ? (value / 1000000).toFixed(1) + 'M' :
                                       value >= 1000 ? (value / 1000).toFixed(1) + 'K' : value;
                            }
                        }
                    },
                    x: {
                        ticks: {
                            autoSkip: false,
                            maxRotation: 45,
                            minRotation: 45
                        }
                    }
                }
            }
        });

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [platformData, selectedPlatform, selectedTeamMember]);

    return (
        <Card>
            <CardHeader>
                <div className="d-flex align-items-center justify-content-between">
                    <h5 className="card-title mb-0">Sum of Total Links by Service</h5>
                    <CardExpandToggler />
                </div>
            </CardHeader>
            <CardBody>
                {platformData.length === 0 ? (
                    <div style={{ height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="text-center">
                            <i className="fas fa-chart-bar fa-3x text-muted mb-3"></i>
                            <p className="text-muted">No data available for the selected filters</p>
                        </div>
                    </div>
                ) : (
                    <div style={{ height: '500px' }}>
                        <canvas ref={chartRef}></canvas>
                    </div>
                )}
            </CardBody>
        </Card>
    );
});

export default PerformanceChart;
